import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from '@/i18n/I18nContext';
import { LanguageSelector } from '@/components/LanguageSelector';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';
import useLegalConfig from '@/hooks/useLegalConfig';

export default function Signup() {
  const navigate = useNavigate();
  const { signup } = useAuth();
  const { t } = useTranslation();
  const { config: legalConfig, loading: legalLoading, error: legalError } = useLegalConfig();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    tenantName: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.email || !formData.password || !formData.tenantName) {
      setError(t('auth.allFieldsRequired'));
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setError(t('auth.passwordMismatch'));
      return;
    }
    if (formData.password.length < 8) {
      setError(t('auth.passwordTooShort'));
      return;
    }
    if (!legalConfig || legalError) {
      setError(t('signup.legalUnavailable'));
      return;
    }
    if (!legalAccepted) {
      setError(t('signup.legalRequired'));
      return;
    }

    setLoading(true);
    try {
      await signup(formData.email, formData.password, formData.tenantName, {
        legal_accepted: true,
        terms_version: legalConfig.terms_version,
        privacy_version: legalConfig.privacy_version,
        dpa_version: legalConfig.dpa_version,
      });
      toast.success(t('auth.signupSuccess'));
      try {
        await api.post('/auth/verify-email/send');
        toast.success(t('verifyEmail.sent'));
      } catch (verificationError) {
        console.warn('Verification email could not be sent immediately.');
      }
      navigate('/wizard');
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : detail?.message || t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  if (!legalLoading && legalConfig?.launch?.public_signup_enabled === false) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="flex items-center justify-center space-x-2 mb-8">
            <MessageSquare className="w-10 h-10 text-emerald-600" />
            <span className="text-3xl font-bold text-slate-900">EVA AI</span>
          </div>
          <Card data-testid="private-pilot-gate">
            <CardHeader className="text-center">
              <div className="flex justify-end mb-2"><LanguageSelector /></div>
              <CardTitle className="text-2xl">{t('signup.privatePilotTitle')}</CardTitle>
              <CardDescription>{t('signup.privatePilotDesc')}</CardDescription>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={() => navigate('/login')}>
                {t('signup.privatePilotLogin')}
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center justify-center space-x-2 mb-8">
          <MessageSquare className="w-10 h-10 text-emerald-600" />
          <span className="text-3xl font-bold text-slate-900">EVA AI</span>
        </div>

        <Card data-testid="signup-form">
          <CardHeader className="text-center">
            <div className="flex justify-end mb-2"><LanguageSelector /></div>
            <CardTitle className="text-2xl">{t('signup.title')}</CardTitle>
            <CardDescription>{t('signup.subtitle')}</CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <Alert variant="destructive" className="mb-6" data-testid="alert-error">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}
            {legalConfig && !legalConfig.commercial_ready && (
              <Alert className="mb-6">
                <AlertDescription>{t('signup.nonCommercialLegal')}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="tenantName" data-testid="label-tenant-name">{t('auth.tenantName')}</Label>
                <Input id="tenantName" name="tenantName" type="text" placeholder="Il mio studio"
                  value={formData.tenantName} onChange={handleChange} data-testid="input-tenant-name" disabled={loading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" data-testid="label-email">{t('auth.email')}</Label>
                <Input id="email" name="email" type="email" placeholder="nome@example.com"
                  value={formData.email} onChange={handleChange} data-testid="input-email" disabled={loading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" data-testid="label-password">{t('auth.password')}</Label>
                <Input id="password" name="password" type="password" placeholder="••••••••"
                  value={formData.password} onChange={handleChange} data-testid="input-password" disabled={loading} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" data-testid="label-confirm-password">{t('auth.confirmPassword')}</Label>
                <Input id="confirmPassword" name="confirmPassword" type="password" placeholder="••••••••"
                  value={formData.confirmPassword} onChange={handleChange} data-testid="input-confirm-password" disabled={loading} />
              </div>

              <label className="flex items-start gap-3 rounded-lg border border-gray-200 p-4 text-sm text-slate-700">
                <input
                  type="checkbox"
                  checked={legalAccepted}
                  onChange={(e) => { setLegalAccepted(e.target.checked); setError(''); }}
                  disabled={loading || legalLoading || legalError}
                  className="mt-1"
                  data-testid="checkbox-legal-acceptance"
                />
                <span>
                  {t('signup.legalConsentPrefix')}{' '}
                  <a href="/terms" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">{t('signup.termsOfService')}</a>,{' '}
                  <a href="/dpa" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">DPA</a>{' '}
                  {t('signup.legalConsentAndPrivacy')}{' '}
                  <a href="/privacy" target="_blank" rel="noreferrer" className="text-emerald-600 hover:underline">{t('signup.privacyPolicy')}</a>.
                </span>
              </label>

              <Button type="submit" className="w-full" size="lg"
                disabled={loading || legalLoading || legalError}
                data-testid="button-signup">
                {loading ? t('signup.creating') : legalLoading ? t('common.loading') : t('signup.button')}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              {t('auth.hasAccount')}{' '}
              <a href="/login" className="text-emerald-600 hover:underline" data-testid="link-login">{t('auth.login')}</a>
            </div>
          </CardContent>
        </Card>

        {legalConfig && (
          <p className="text-center text-xs text-slate-500 mt-6">
            Terms {legalConfig.terms_version} · Privacy {legalConfig.privacy_version} · DPA {legalConfig.dpa_version} · {legalConfig.effective_date}
          </p>
        )}
      </div>
    </div>
  );
}
